import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useRoute } from 'vue-router';

import { GetSalaryHistoryDetails } from '../services/SalaryHistory/GetSalaryHistoryDetails';
import { formatDateISO, parseDate } from '../helpers/dateParser';

export const useSalaryHistoryStore = defineStore('salaryHistory', () => {
  const route = useRoute();

  const employeeProfileModule = ref(route.meta.employeeProfileModule);

  const paramEmployeeId = ref(route.params.empId);

  const isFeatureFlagOn = ref(false);

  const employeeName = ref('-');

  const filterCount = ref({
    all: 0,
    current: 0,
    past: 0,
  });

  const table = ref({
    header: [
      'EFFECTIVITY DATE',
      'BASE SALARY',
      'DE MINIMIS',
      'DAILY ALLOWANCE',
      'MONTHLY GROSS',
      'REASON',
      '',
      '',
    ],
    data: [],
    loader: false,
    filter: 'all',
    search: null,
  });

  const setTableData = (routeName, paramEmployeeID) => {
    table.value.loader = true;

    GetSalaryHistoryDetails({
      employeeID: paramEmployeeID,
      ExcludePending: routeName === 'salary-history',
    })
      .then((response) => {
        employeeName.value = response.lastName + ', ' + response.firstName;
        table.value.data = response.salaryHistory;
        table.value.loader = false;

        if (routeName === 'manage-salary-details') {
          filterCount.value.all = response.salaryHistory.length;

          filterCount.value.current = response.salaryHistory.filter(
            (data) => data.status === 'current',
          ).length;

          filterCount.value.past = response.salaryHistory.filter(
            (data) => data.status === 'past',
          ).length;

          let modifiedTableDataStatus = response.salaryHistory.map((fields) => {
            return {
              ...fields,
              status: fields.status === 'pending' ? 'upcoming' : fields.status,
            };
          });

          table.value.data = modifiedTableDataStatus;
        }
      })
      .catch((error) => {
        console.error(error);

        if (error.response.data.message) {
          emptyState.value.title = error.response.data.message;
          emptyState.value.description = error.message;
        }

        table.value.loader = false;
      });
  };

  const modifiedStatusTableData = (response, filter) => {
    let salaryHistory = response;

    if (filter !== 'all') {
      let filteredData = salaryHistory.filter((data) => data.status === filter);

      table.value.data = filteredData.map((fields) => {
        return {
          ...fields,
          status: fields.status === 'pending' ? 'upcoming' : fields.status,
        };
      });
    } else {
      table.value.data = salaryHistory.map((fields) => {
        return {
          ...fields,
          status: fields.status === 'pending' ? 'upcoming' : fields.status,
        };
      });
    }

    filterCount.value.all = salaryHistory.length;

    filterCount.value.current = salaryHistory.filter(
      (data) => data.status === 'current',
    ).length;

    filterCount.value.past = salaryHistory.filter(
      (data) => data.status === 'past',
    ).length;

    table.value.loader = false;
  };

  const resetTable = () => {
    table.value = {
      header: [
        'EFFECTIVITY DATE',
        'BASE SALARY',
        'DE MINIMIS',
        'DAILY ALLOWANCE',
        'MONTHLY GROSS',
        'REASON',
        '',
        '',
      ],
      data: [],
      loader: false,
      filter: 'all',
      search: null,
    };
  };

  const emptyState = ref({
    title: 'No Salary History',
    description: 'Add salary to show information here.',
  });

  const sidenav = ref({
    state: false,
    salaryID: null,
    mode: 'side',
    type: null,
    selectedData: null,
    loader: false,
  });

  const setSidenav = (state, salaryID = null, selectedData = null) => {
    sidenav.value.state = state;

    if (state) {
      sidenav.value.loader = true;

      setSideNavData(salaryID, selectedData, false);

      if (sidenav.value.type === 'edit') {
        mapEditData(sidenav.value.selectedData);
      }
    } else {
      setSideNavData(null, null, false);
    }
  };

  const setSideNavData = (salaryID, selectedData, loader) => {
    sidenav.value.salaryID = salaryID;
    sidenav.value.selectedData = selectedData;
    sidenav.value.loader = loader;
  };

  const convertISODate = (date) => {
    return formatDateISO(parseDate(date));
  };

  const setSelectedOption = (selectedReason) => {
    const newArray = salaryForm.value.reason.options.map((item) => {
      return {
        ...item,
        selected: item.value === selectedReason,
      };
    });

    return newArray;
  };

  const mapEditData = (selectedData) => {
    salaryForm.value.effectivityDate.isError = false;
    salaryForm.value.effectivityDate.isErrorExistingDate = false;
    salaryForm.value.effectivityDate.isErrorAfterDate = false;
    salaryForm.value.effectivityDate.errorMessage = null;

    salaryForm.value.effectivityDate.value = convertISODate(
      selectedData.effectivityDate,
    );
    salaryForm.value.baseSalary.value = selectedData.basicSalary.toFixed(2);
    salaryForm.value.deMinimis.value = selectedData.deMinimis.toFixed(2);
    salaryForm.value.dailyAllowance.value =
      selectedData.dailyAllowance.toFixed(2);
    salaryForm.value.monthlyGross =
      selectedData.basicSalary.toFixed(2) + selectedData.deMinimis.toFixed(2);
    salaryForm.value.reason.value = selectedData.reason;
    salaryForm.value.reason.options = setSelectedOption(selectedData.reason);
    salaryForm.value.breakdownAllowances.othersData =
      selectedData.otherData.map((item) => {
        return {
          id: item.name.charAt(0).toLowerCase() + item.name.slice(1),
          name: item.name.charAt(0).toUpperCase() + item.name.slice(1),
          value: item.value,
        };
      });
  };

  const resetSidenav = () => {
    sidenav.value = {
      state: false,
      salaryID: null,
      mode: 'side',
      type: null,
      selectedData: null,
      loader: false,
    };

    resetSalaryForm();
  };

  const setOthersData = () => {
    let otherDataFields = [
      'clothing',
      'communication',
      'discretionary',
      'laundry',
      'meal',
      'medical',
      'productivity',
      'rice',
      'transportation',
      'travel',
      'others',
      'notes',
    ];

    let modifiedOtherDataFields = [];

    otherDataFields.forEach((field) => {
      modifiedOtherDataFields.push({
        id: field,
        name: field.charAt(0).toUpperCase() + field.slice(1),
        value: '',
      });
    });

    return modifiedOtherDataFields;
  };

  const salaryForm = ref({
    effectivityDate: {
      value: null,
      isError: false,
      isErrorExistingDate: false,
      isErrorAfterDate: false,
      errorMessage: null,
    },
    baseSalary: {
      value: parseFloat(0).toFixed(2),
      isError: false,
      errorMessage: null,
    },
    deMinimis: {
      value: parseFloat(0).toFixed(2),
      isError: false,
      errorMessage: null,
    },
    dailyAllowance: {
      value: parseFloat(0).toFixed(2),
      isError: false,
      errorMessage: null,
    },
    monthlyGross: parseFloat(0).toFixed(2),
    reason: {
      value: null,
      options: [
        {
          text: 'Select Reason',
          value: null,
          selected: true,
          disabled: true,
        },
        { text: 'Hiring Anniversary', value: 'Hiring Anniversary' },
        { text: 'Regularization', value: 'Regularization' },
        { text: 'Salary Increase', value: 'Salary Increase' },
        { text: 'Transfer', value: 'Transfer' },
        { text: 'Probationary', value: 'Probationary' },
        { text: 'Promotion', value: 'Promotion' },
      ],
      isError: false,
      errorMessage: null,
    },
    breakdownAllowances: {
      accordion: null,
      othersData: setOthersData(),
    },
  });

  const resetSalaryForm = () => {
    salaryForm.value = {
      effectivityDate: {
        value: null,
        isError: false,
        isErrorExistingDate: false,
        isErrorAfterDate: false,
        errorMessage: null,
      },
      baseSalary: {
        value: parseFloat(0).toFixed(2),
        isError: false,
        errorMessage: null,
      },
      deMinimis: {
        value: parseFloat(0).toFixed(2),
        isError: false,
        errorMessage: null,
      },
      dailyAllowance: {
        value: parseFloat(0).toFixed(2),
        isError: false,
        errorMessage: null,
      },
      monthlyGross: parseFloat(0).toFixed(2),
      reason: {
        value: null,
        options: [
          {
            text: 'Select Reason',
            value: null,
            selected: true,
            disabled: true,
          },
          { text: 'Hiring Anniversary', value: 'Hiring Anniversary' },
          { text: 'Regularization', value: 'Regularization' },
          { text: 'Salary Increase', value: 'Salary Increase' },
          { text: 'Transfer', value: 'Transfer' },
          { text: 'Probationary', value: 'Probationary' },
          { text: 'Promotion', value: 'Promotion' },
        ],
        isError: false,
        errorMessage: null,
      },
      breakdownAllowances: {
        accordion: null,
        othersData: setOthersData(),
      },
    };
  };

  const modals = ref({
    addEditSalaryConfirmation: false,
    deleteSalaryConfirmation: false,
  });

  const toasts = ref({
    addSalarySuccess: false,
    deleteSalarySucccess: false,
    editSalarySuccess: false,
  });

  const deleteSalaryHistoryEntry = ref({});
  const accessLevelStatus = ref(null);
  const isAccessLevelViewOnly = ref(null);

  return {
    isFeatureFlagOn,
    employeeName,
    employeeProfileModule,
    paramEmployeeId,
    deleteSalaryHistoryEntry,
    filterCount,
    table,
    emptyState,
    setTableData,
    modifiedStatusTableData,
    resetTable,
    sidenav,
    setSidenav,
    resetSidenav,
    salaryForm,
    resetSalaryForm,
    modals,
    toasts,
    accessLevelStatus,
    isAccessLevelViewOnly,
  };
});
